import { useState } from 'react'
import { Card, Tag, Button, Space, Table, Empty, Spin, Typography, Alert } from 'antd'
import { EditOutlined, LinkOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { hospitalsApi } from '@/api/hospitals'
import type { SSOPermissions } from '@/types/hospital'
import { SSOEditModal } from './SSOEditModal'
import { getApiErrorMessage } from '@/utils/error'

const { Text } = Typography

interface SSOConfigurationProps {
  hospitalId: string
}

const permissionLabels: Record<keyof SSOPermissions, string> = {
  bi: 'BI Access',
  sender: 'Sender',
  readonly: 'Read Only',
  receiver: 'Receiver',
  dashboard: 'Dashboard',
  teamInsights: 'Team Insights',
}

export const SSOConfiguration = ({ hospitalId }: SSOConfigurationProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['hospital-sso', hospitalId],
    queryFn: async () => {
      const response = await hospitalsApi.getSSOConfig(hospitalId)
      return response.data
    },
  })

  const ssoConfig = data?.ssoConfig
  const metadata = data?.metadata

  if (isLoading) {
    return (
      <Card title="SSO Configuration" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card title="SSO Configuration" style={{ marginBottom: 16 }}>
        <Alert
          message="Error loading SSO configuration"
          description={
            getApiErrorMessage(error, 'Failed to load SSO configuration')
          }
          type="error"
          showIcon
        />
      </Card>
    )
  }

  if (!ssoConfig) {
    return (
      <Card
        title="SSO Configuration"
        extra={
          <Button icon={<EditOutlined />} onClick={() => setIsEditModalOpen(true)}>
            Configure SSO
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Empty description="No SSO configuration" />
        <SSOEditModal
          hospitalId={hospitalId}
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          existingConfig={null}
        />
      </Card>
    )
  }

  const permissionsData = ssoConfig.permissions
    ? Object.entries(ssoConfig.permissions)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => ({
          key,
          permission: permissionLabels[key as keyof SSOPermissions] || key,
          enabled: value,
        }))
    : []

  return (
    <>
      <Card
        title="SSO Configuration"
        extra={
          <Button icon={<EditOutlined />} onClick={() => setIsEditModalOpen(true)}>
            Edit
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Status: </Text>
            {ssoConfig.enabled ? (
              <Tag color="success">Enabled</Tag>
            ) : (
              <Tag color="default">Disabled</Tag>
            )}
          </div>

          {ssoConfig.connection && (
            <div>
              <Text strong>Connection: </Text>
              <Text code>{ssoConfig.connection}</Text>
              {metadata?.dashboardUrl && (
                <Button
                  type="link"
                  size="small"
                  icon={<LinkOutlined />}
                  href={metadata.dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: 8 }}
                >
                  View in Auth0
                </Button>
              )}
            </div>
          )}

          {ssoConfig.group && (
            <div>
              <Text strong>SAML Group: </Text>
              <Tag>{ssoConfig.group}</Tag>
            </div>
          )}

          {permissionsData.length > 0 && (
            <div>
              <Text strong>Default Permissions:</Text>
              <Table
                dataSource={permissionsData}
                columns={[
                  {
                    title: 'Permission',
                    dataIndex: 'permission',
                    key: 'permission',
                  },
                  {
                    title: 'Enabled',
                    dataIndex: 'enabled',
                    key: 'enabled',
                    render: (enabled: boolean) =>
                      enabled ? <Tag color="success">Yes</Tag> : <Tag color="default">No</Tag>,
                  },
                ]}
                pagination={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            </div>
          )}
        </Space>
      </Card>

      <SSOEditModal
        hospitalId={hospitalId}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        existingConfig={ssoConfig}
      />
    </>
  )
}
